import { Archive, FileText, Folder, Inbox, SendHorizonal, ShieldAlert, Trash2 } from "lucide-react";
import { useMemo } from "react";

import { cn } from "@/lib/utils";
import type { Mailbox } from "../core/shell-types";

const ROLE_ORDER: Record<string, number> = {
  inbox: 0,
  drafts: 1,
  sent: 2,
  archive: 3,
  junk: 4,
  trash: 5,
};

function roleIcon(role: string | null | undefined) {
  switch ((role ?? "").toLowerCase()) {
    case "inbox":
      return Inbox;
    case "drafts":
      return FileText;
    case "sent":
      return SendHorizonal;
    case "archive":
      return Archive;
    case "junk":
      return ShieldAlert;
    case "trash":
      return Trash2;
    default:
      return Folder;
  }
}

export interface MailboxSection {
  key: string;
  title: string;
  mailboxes: Mailbox[];
}

// The snapshot carries every mailbox the active account can see in one flat
// list; the account's own mailboxes come first, then one section per shared
// account (Stalwart exposes mailboxes other principals shared with you).
export function groupMailboxes(mailboxes: Mailbox[], ownerTitle: string): MailboxSection[] {
  const own: Mailbox[] = [];
  const shared = new Map<string, MailboxSection>();
  for (const mailbox of mailboxes) {
    if (!mailbox.isShared) {
      own.push(mailbox);
      continue;
    }
    const key = mailbox.accountId ?? mailbox.accountName ?? "shared";
    const section = shared.get(key) ?? {
      key: `shared:${key}`,
      title: mailbox.accountName ?? mailbox.accountId ?? "Shared",
      mailboxes: [],
    };
    section.mailboxes.push(mailbox);
    shared.set(key, section);
  }
  return [{ key: "own", title: ownerTitle, mailboxes: own }, ...shared.values()];
}

interface TreeNode {
  mailbox: Mailbox;
  depth: number;
}

function flattenTree(mailboxes: Mailbox[]): TreeNode[] {
  const children = new Map<string | null, Mailbox[]>();
  const ids = new Set(mailboxes.map((mailbox) => mailbox.id));
  for (const mailbox of mailboxes) {
    // Orphans (parent outside this section) hang off the root.
    const key = mailbox.parentId && ids.has(mailbox.parentId) ? mailbox.parentId : null;
    const bucket = children.get(key) ?? [];
    bucket.push(mailbox);
    children.set(key, bucket);
  }
  for (const bucket of children.values()) {
    bucket.sort((a, b) => {
      const roleA = ROLE_ORDER[(a.role ?? "").toLowerCase()] ?? 10;
      const roleB = ROLE_ORDER[(b.role ?? "").toLowerCase()] ?? 10;
      if (roleA !== roleB) {
        return roleA - roleB;
      }
      if (a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder;
      }
      return a.name.localeCompare(b.name);
    });
  }
  const nodes: TreeNode[] = [];
  const visit = (parentId: string | null, depth: number) => {
    for (const mailbox of children.get(parentId) ?? []) {
      nodes.push({ mailbox, depth });
      visit(mailbox.id, depth + 1);
    }
  };
  visit(null, 0);
  return nodes;
}

export function MailboxList({
  compact = false,
  mailboxes,
  ownerTitle,
  selectedMailboxId,
  onSelect,
}: {
  // Compact: the list is a full-width pane of its own (44px rows).
  compact?: boolean;
  mailboxes: Mailbox[];
  ownerTitle: string;
  selectedMailboxId: string | null;
  onSelect: (mailboxId: string) => void;
}) {
  const sections = useMemo(
    () =>
      groupMailboxes(mailboxes, ownerTitle).map((section) => ({
        ...section,
        nodes: flattenTree(section.mailboxes),
      })),
    [mailboxes, ownerTitle],
  );

  return (
    <nav
      aria-label="Mailboxes"
      className={cn("shrink-0 overflow-y-auto py-3", compact ? "w-full" : "w-56")}
    >
      {sections.map((section, index) => (
        <section key={section.key} className={cn(index > 0 && "mt-5")}>
          {sections.length > 1 || index > 0 ? (
            <h2 className="px-4 pb-1 text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70">
              {section.title}
            </h2>
          ) : null}
          <ul className="px-2">
            {section.nodes.map(({ mailbox, depth }) => {
              const selected = selectedMailboxId === mailbox.id;
              const Icon = roleIcon(mailbox.role);
              return (
                <li key={mailbox.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(mailbox.id)}
                    aria-current={selected ? "true" : undefined}
                    className={cn(
                      "group flex w-full items-center gap-2.5 rounded-md px-2.5 text-left text-sm transition-colors",
                      compact ? "min-h-11 py-2" : "py-1.5",
                      selected
                        ? "bg-accent font-medium text-accent-foreground"
                        : "text-foreground/80 hover:bg-accent/60 hover:text-foreground",
                    )}
                    style={{ paddingLeft: `${0.625 + depth * 0.875}rem` }}
                  >
                    <Icon
                      aria-hidden
                      className={cn(
                        "h-4 w-4 shrink-0",
                        selected
                          ? "text-foreground"
                          : "text-muted-foreground/70 group-hover:text-foreground/70",
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate">{mailbox.name}</span>
                    {mailbox.unreadEmails > 0 ? (
                      <span
                        className={cn(
                          "rounded-full px-1.5 text-xs font-semibold tabular-nums",
                          selected ? "text-foreground" : "text-primary",
                        )}
                      >
                        {mailbox.unreadEmails}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </nav>
  );
}
